import * as React from "react";
import {
  UpdateControl,
  UpdateHeadered,
  UpdateInnerTreeView,
  ColumnsProportion,
  ColumnProportionEm,
  TTreeDataMoveNodeMode,
  CSColumnBase,
  CSTreeColumn,
  TAlignment,
  TTreeDataHasChildNodes,
} from "../common/communication.base";
import { withContext, K2ComponentState, WithContextPlacementProps, StyleHelper, AcquireControl } from "./k2hoc";
import { NclTreeView, NclInnerTreeView, K2TreeViewItem, TreeViewListener, TreeOperations } from "../common/components.ncl";
import { ElementHtmlAttributes, getAttributes } from "../common/common";
import Table, { ColumnType } from "antd/es/table";
import { VisualContext } from "../common/visualContext";
import { K2Header } from "./K2Expander";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import ResizeObserver from "resize-observer-polyfill";
import { HeaderColumn, HeaderColumnProps, K2Cell } from "./K2DataGrid";
import "antd/lib/table/style/index.css";

interface InnerTreeViewState extends K2ComponentState<UpdateInnerTreeView> {
  columns: ColumnType<K2TreeViewItem>[];
  expandedKeys: string[];
  columnIndex: number;
  arrow: string;
  drag: boolean;
  width: number;
  height: number;
  selectedRowKey: string;
  clientWidthDiffs?: number[];
  dragging: boolean;
  xDragPos: number;
  dataSource: K2TreeViewItem[];
  reordered: boolean;
  deleted: boolean;
}

const tableRule = (props: K2RuleWithVCXProps & { width: number }) => ({
  color: "inherit",
  "& .ant-table-body": {
    color: props.inEditMode ? props.vcx.getColor(props.vcx.Data.ColorMap.DataChangeColorFrg) : props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorFrg),
    backgroundColor: props.inEditMode
      ? props.vcx.getColor(props.vcx.Data.ColorMap.DataChangeColorBck)
      : props.vcx.getColor(props.vcx.Data.ColorMap.DataBrowseColorBck),
  },
  "& table": {
    width: props.width + "px",
    height: "min-content",
  },
});

class _InnerTreeView extends React.PureComponent<WithContextPlacementProps, InnerTreeViewState> implements TreeViewListener {
  static displayName = `K2InnerTreeView`;
  private control: NclInnerTreeView;
  private rect: DOMRect;
  private expandedNodes = new Array<any>();
  private draggedNode: K2TreeViewItem;
  private innerTreeViewDiv = React.createRef<HTMLDivElement>();
  private ro: ResizeObserver;
  private columns: DOMRect[];
  private draggedColumnIndex: number;
  private hoveredColumnIndex: number;
  private hackScroll: Element;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclInnerTreeView;
    }) as NclInnerTreeView;
    let expandedKeys: string[] = [];
    if (this.control.Root) {
      expandedKeys = this.control.Root.getExpandedKeys();
    }
    this.state = {
      data: this.control.init(this) as UpdateInnerTreeView,
      vcxVersion: -1,
      columns: undefined,
      expandedKeys: expandedKeys,
      columnIndex: undefined,
      arrow: undefined,
      drag: false,
      width: undefined,
      selectedRowKey: undefined,
      height: 0,
      dragging: false,
      xDragPos: undefined,
      dataSource: null,
      reordered: false,
      deleted: false,
    };
    this.props.renderer.renderStatic(treeViewStyles(this.control.VCX));
  }

  updatePosition(oldRow: string, newRow: string, oldCol: number, newCol: number): void {
    if (oldRow !== newRow) {
      this.setState({ selectedRowKey: newRow }); //pokud se selected nastavoval jen zmenou stavu podle currentbmk, pak nefungoval na prave tlacitko mysi kdy se sice spravne zobrazilo ale tree ukazovalo posledni selektovany radek a opravilo to az po uzavreni popupu.
    }
  }

  updateState(state: UpdateInnerTreeView) {
    if (state.ComputedColumnsVersion !== state.ColumnsVersion && this.state.width > 0) {
      this.control.reCalculateColumnWidths(this.state.width);
      return; // recalculate columns width and call update
    }

    this.setState((prevState: InnerTreeViewState) => {
      let diffs = prevState.clientWidthDiffs;
      if (state.ColumnsVersion != prevState.data.ColumnsVersion) {
        diffs = undefined;
      }
      return { data: state as UpdateInnerTreeView, clientWidthDiffs: diffs };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.ro.unobserve(this.innerTreeViewDiv.current);
    this.control.willUnMount(true);
    this.control = null;
    if (this.timeout) clearTimeout(this.timeout);
    if (this.hackScroll) {
      this.hackScroll.removeEventListener("wheel", this.handleHackWheel);
    }
  }

  componentDidMount() {
    this.ro = new ResizeObserver((entries, observer) => {
      this.setState({ height: entries[0].contentRect.height, width: entries[0].contentRect.width });

      if (this.state.data.ComputedColumnsVersion && this.state.data.ComputedColumnsVersion === this.state.data.ColumnsVersion) {
        this.setState({ columns: this.convertColumns(this.state.data.ColumnsProportion), dataSource: this.control.Root.children } as InnerTreeViewState);
      }
    });

    this.ro.observe(this.innerTreeViewDiv.current);

    if (this.control && this.control.RowBmk) {
      this.updatePosition(undefined, this.control.RowBmk, undefined, this.control.ColNdx);
    }
  }

  componentDidUpdate(prevProps: WithContextPlacementProps, prevState: InnerTreeViewState) {
    if (
      this.state.width > 0 &&
      (!this.state.data.ColumnsVersion ||
        this.state.data.ComputedColumnsVersion !== this.state.data.ColumnsVersion ||
        (!this.state.clientWidthDiffs && prevState.width != this.state.width && this.state.data.AutoAdjustWidth))
    ) {
      this.control.reCalculateColumnWidths(this.state.width);
      return;
    }

    let expandedKeys: string[] = undefined;
    if (prevState.data.DataVersion !== this.state.data.DataVersion) {
      expandedKeys = this.control.Root.getExpandedKeys();
    }

    let newState: InnerTreeViewState = null;
    if (prevState.data.ComputedColumnsVersion !== this.state.data.ComputedColumnsVersion || prevState.clientWidthDiffs !== this.state.clientWidthDiffs) {
      newState = { columns: this.convertColumns(this.state.data.ColumnsProportion) } as InnerTreeViewState;
    }

    if (expandedKeys) {
      newState = Object.assign({}, newState, { expandedKeys: expandedKeys }) as InnerTreeViewState;
    }

    if (this.state.deleted) {
      this.hoveredColumnIndex = undefined;
      this.setState({ ...newState, dragging: false, deleted: false, xDragPos: undefined });
    } else if (this.state.reordered) {
      this.hoveredColumnIndex = undefined;
      this.setState({ ...newState, dragging: false, reordered: false, xDragPos: undefined });
    }

    if (newState) {
      //newState = { ...newState, dataSource: this.control.Root.children };
      this.setState(
        newState,
        prevState.data.DataVersion !== this.state.data.DataVersion
          ? () => {
              this.setState({ dataSource: undefined }, () => {
                this.setState({ dataSource: this.control.Root.children });
              });
            }
          : undefined
      );
    }

    if (this.state.selectedRowKey !== prevState.selectedRowKey) {
      let current = document.querySelector(`[data-row-key='${this.state.selectedRowKey}']`);
      if (current) {
        current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      }
    }

    if (this.state.columns && this.state.columns.length !== 0 && !this.hackScroll && this.innerTreeViewDiv) {
      let result = this.innerTreeViewDiv.current.getElementsByClassName("ant-table-body");
      if (result && result.length === 1) {
        this.hackScroll = result[0];
        this.hackScroll.addEventListener("wheel", this.handleHackWheel);
      }
    }
  }

  private handleHackWheel = (e: Event) => {
    e.preventDefault();
  };

  setColumns = (options: { sizes: DOMRect[]; draggedColumnIndex: number }) => {
    this.columns = options.sizes;
    this.draggedColumnIndex = options.draggedColumnIndex;
    this.setState({ dragging: true });
  };

  private convertColumns(ColumnsProportion: ColumnsProportion): ColumnType<K2TreeViewItem>[] {
    if (ColumnsProportion) {
      let columns: Array<ColumnType<K2TreeViewItem>> = new Array<ColumnType<K2TreeViewItem>>();
      if (ColumnsProportion.length > 0) {
        ColumnsProportion.map((value, index, list) => {
          columns.push(this.createColumn(value, index, list.length));
        });
      }

      return columns;
    }

    return null;
  }

  private calcColumnWidth(proportion: ColumnProportionEm, index: number): number {
    let diff: number = 0;
    if (this.state.clientWidthDiffs && index < this.state.clientWidthDiffs.length) {
      diff = this.state.clientWidthDiffs[index];
    }

    return proportion.Width + diff;
  }

  private calcColumnsWidth = () => {
    let tableWidth = 0;

    for (let i = 0; i < this.state.data.ColumnsProportion.length; i++) {
      tableWidth += this.calcColumnWidth(this.state.data.ColumnsProportion[i], i);
    }

    return tableWidth;
  };

  private createColumn(value: ColumnProportionEm, index: number, columns: number): ColumnType<K2TreeViewItem> {
    return {
      // title: (value) ? value.Caption : '',
      dataIndex: index,
      render: (column: CSColumnBase, record: K2TreeViewItem, i: number) => {
        if (record && record.columns && record.columns.length > index) {
          return this.renderNode(record.columns[index], record, value, index);
        }
        return null;
      },

      width: value?.Width ? this.calcColumnWidth(value, index) : 100,
      onHeaderCell: (column: ColumnType<K2TreeViewItem>): HeaderColumnProps => {
        return {
          columnNdx: index,
          columnProportion: value,
          onColumnContextMenu: this.handleHeaderColumnContextMenu,
          onColumnClick: () => {},
          fixed: false,
          vcx: this.control.VCX,
          onColumnResize: this.handleColumnResize,
          isTreeCell: true,
          setColumns: this.setColumns,
          deleteColumn: this.deleteColumn,
          renderer: this.props.renderer,
        };
      },
    };
  }

  private handleHeaderColumnContextMenu = (rowIndex: number, colIndex: number) => {
    this.control.contextMenu("", colIndex);
  };

  private handleColumnResize = (colIndex: number, diffX: number, isCompleted: boolean) => {
    let diffs: Array<number>;
    if (isCompleted) {
      if (this.state.data.ColumnsProportion && colIndex < this.state.data.ColumnsProportion.length) {
        if (this.state.clientWidthDiffs) {
          diffs = [...this.state.clientWidthDiffs];
        } else {
          diffs = [];
          this.state.data.ColumnsProportion.map((value, i) => {
            diffs[i] = 0;
          });
        }
      }
      if (diffs) {
        diffs[colIndex] += diffX;
        if (this.state.data.ColumnsProportion[colIndex].Width + diffs[colIndex] <= 10) return; //check minimum
        this.control.setColumnsWidth(diffs, this.state.data.ColumnsProportion);
        this.setState({ clientWidthDiffs: diffs });
      }
    }
  };

  private renderNode(value: CSColumnBase, node: K2TreeViewItem, proportion: ColumnProportionEm, colNdx: number): JSX.Element | string {
    return (
      <div
        className="border-transparent"
        style={{ height: `${this.control.VCX.GridControl.GetRowHeight(1)}px`, borderLeft: colNdx === 0 ? "1px solid transparent" : null }}
        draggable={this.state.data.IsDraggable}
        onDragEnter={(e) => this.handleDragEnter(e, node)}
        onDragStart={(e) => this.handleDragStart(e, node)}
        onDragOver={(e) => this.handleDragOver(e, node)}
        onDragLeave={this.handleDragLeave}
        onDrop={(e) => this.handleDrop(e, node)}
      >
        <K2Cell
          vcx={this.control.VCX}
          text={value.Text}
          glyphId={value.GlyphId}
          glyphId2={(value as CSTreeColumn).StateGlyphId}
          isSelectedText={false}
          isSelectedCell={false}
          isSelectedRow={this.state.selectedRowKey === node.key}
          isCancelled={false}
          alignment={proportion.Alignment}
          aligmentText={colNdx === 0 ? TAlignment.taRightJustify : proportion.Alignment}
          condFormatting={value.CondFormatting}
          isFixed={false}
        />
      </div>
    );
  }

  handleDragStart = (e: React.DragEvent, node: K2TreeViewItem) => {
    e.dataTransfer.setData("text/plain", node.key);
    this.draggedNode = node;
  };

  handleDragEnter = (e: React.DragEvent, node: K2TreeViewItem) => {
    if (this.state.dragging && this.draggedNode && this.draggedNode.key === node.key) return;
    if (this.expandedNodes.length > 0) {
      clearTimeout(this.expandedNodes.pop());
    }
    this.rect = e.currentTarget.getBoundingClientRect();
    if (this.expandedNodes.length === 0 && node.Data.HasChildren !== TTreeDataHasChildNodes.chnNo && node.expanded !== true) {
      let delayID = setTimeout(() => {
        this.control.executeExpand(node.key);
        this.expandedNodes.pop();
      }, 1000);

      this.expandedNodes.push(delayID);
    }
  };

  handleDrop = (e: React.DragEvent, node: K2TreeViewItem) => {
    e.preventDefault();
    if (this.state.dragging) return;

    e.currentTarget.className = "border-transparent";

    if ((this.draggedNode && node.key === this.draggedNode.key) || node.parent.key === this.draggedNode.key) return;
    if (this.expandedNodes.length > 0) {
      clearTimeout(this.expandedNodes.pop());
    }

    this.control.executeMove(e.dataTransfer.getData("text/plain"), node.key, this.calcDropPosition(e));
    this.setState({ selectedRowKey: e.dataTransfer.getData("text/plain") });
  };

  draggingParentIntoChildren = (node: K2TreeViewItem): boolean => {
    if (this.draggedNode.key === node.key) {
      return true;
    } else if (this.draggedNode.children && this.draggedNode.children.length > 0) {
      const found = node.parent && node.parent.children.findIndex((childNode: K2TreeViewItem) => childNode.parent.key === this.draggedNode.key);
      if (found === null) {
        return false;
      } else if (found > -1) {
        return true;
      } else {
        return this.draggingParentIntoChildren(node.parent);
      }
    } else {
      return false;
    }
  };

  handleDragOver = (e: React.DragEvent, node: K2TreeViewItem) => {
    if (this.state.dragging) return;
    if (this.draggingParentIntoChildren(node)) {
      this.setState({ selectedRowKey: node.key });
      return;
    }

    e.preventDefault();

    const target = e.currentTarget;
    let position = this.calcDropPosition(e);

    switch (position) {
      case TTreeDataMoveNodeMode.tdmnOnNode:
        target.className = "border-select-node";
        break;
      case TTreeDataMoveNodeMode.tdmnBeforeNode:
        target.className = "border-top";
        break;
      case TTreeDataMoveNodeMode.tdmnAfterNode:
        target.className = "border-bottom";
        break;
      default:
        break;
    }
  };

  private calcDropPosition(e: React.DragEvent): TTreeDataMoveNodeMode {
    if (e.clientY >= Math.floor(this.rect.top) - 1 && e.clientY < this.rect.top + 5) {
      return TTreeDataMoveNodeMode.tdmnBeforeNode;
    } else if (e.clientY > this.rect.bottom - 5 && e.clientY < this.rect.bottom) {
      return TTreeDataMoveNodeMode.tdmnAfterNode;
    } else {
      return TTreeDataMoveNodeMode.tdmnOnNode;
    }
  }

  handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.className = "border-transparent";
  };

  onExpand = (expanded: boolean, record: K2TreeViewItem) => {
    this.control.executeExpand(record.key);
  };

  handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  handleDragEnterColumns = (e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!this.state.dragging) return;
    let xPos = 0;

    if (e.nativeEvent instanceof DragEvent) {
      xPos = (e as React.DragEvent).clientX;
    } else {
      xPos = (e as React.TouchEvent).touches[0].clientX;
    }

    let offsetLeft = e.currentTarget.getBoundingClientRect().left;
    this.columns.map((column, index) => {
      if (xPos < column.right && (index < this.state.data.FixedColumnsCount || index < 1)) return;
      if (xPos > column.left && xPos < column.right) {
        if (this.draggedColumnIndex >= index) {
          this.setState({ xDragPos: column.left - offsetLeft });
          this.hoveredColumnIndex = index;
        } else {
          this.setState({ xDragPos: column.right - offsetLeft });
          this.hoveredColumnIndex = index;
        }
      }
    });
  };

  handleDropColumn = () => {
    if (!this.state.dragging) return;
    this.control.columnMove(this.draggedColumnIndex, this.hoveredColumnIndex, true);
    this.setState({ dragging: false });
  };

  deleteColumn = () => {
    if (!this.draggedColumnIndex || this.draggedColumnIndex === 0) return;

    this.control.columnMove(this.draggedColumnIndex, -1, true);
    this.setState({ dragging: false });
  };

  private timeout: any;
  private clickCount: number = 0;

  render() {
    return (
      <div
        ref={this.innerTreeViewDiv}
        style={StyleHelper(this.control, { flex: "1 1 auto", position: "relative", height: this.state.height + "px" })}
        className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
        onDragOver={this.handleColumnDragOver}
        onDragEnter={this.handleDragEnterColumns}
        onDrop={this.handleDropColumn}
        onTouchMove={this.handleDragEnterColumns}
        onTouchEnd={this.handleDropColumn}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onWheel={this.handleWheel}
      >
        {this.state.dragging && this.hoveredColumnIndex !== undefined && (
          <div style={{ border: "1px solid", position: "absolute", left: this.state.xDragPos, height: "calc(100% - 24px)", zIndex: 1 }}></div>
        )}
        {this.state.data.DeniedText && (
          <div
            style={{
              position: "absolute",
              width: "99%",
              height: this.control.VCX.sizeMap(20) + "px",
              zIndex: 1,
              top: this.control.VCX.sizeMap(25) + "px",
              backgroundColor: "red",
              color: "white",
              justifyContent: "center",
            }}
          >
            {this.state.data.DeniedText}
          </div>
        )}
        {this.state.columns && this.state.columns.length !== 0 && (
          <Table
            className={this.props.renderer.renderRule(tableRule, {
              vcx: this.control.VCX,
              width: this.calcColumnsWidth(),
              inEditMode: this.control.InEditMode,
            })}
            showHeader={this.state.columns.length === 1 ? false : true}
            scroll={{ y: this.state.height }}
            tableLayout="fixed"
            locale={{ emptyText: this.control.Tree.Ncl.NoDataText }}
            expandable={{
              childrenColumnName: "children",
              expandIconColumnIndex: 0,
              onExpand: this.onExpand,
              expandedRowKeys: this.state.expandedKeys,
            }}
            size="small"
            dataSource={this.state.dataSource}
            columns={this.state.columns}
            pagination={false}
            rowClassName={(record: K2TreeViewItem) => {
              if (this.state.data.DeniedText) {
                return "antd-denied-text";
              }
            }}
            onRow={(record: K2TreeViewItem) => {
              return {
                tabIndex: 1,
                onClick: (e) => {
                  if (this.timeout) clearTimeout(this.timeout);

                  this.clickCount++;
                  let target = e.currentTarget;

                  this.timeout = setTimeout(() => {
                    if (this.clickCount >= 2) {
                      this.control.click(record.key, findColumnIndex(target), 2);
                    } else {
                      this.control.click(record.key, findColumnIndex(target), 1);
                    }
                    this.clickCount = 0;
                  }, 250);
                  e.preventDefault();
                },
                onContextMenu: (e) => {
                  this.control.contextMenu(record.key, findColumnIndex(e.target as HTMLElement));
                  e.preventDefault();
                },
                onFocus: (e) => {
                  this.control.Tree.setActiveControlRequested();
                  e.stopPropagation();
                },
              };
            }}
            components={{ header: { cell: HeaderColumn } }}
          />
        )}
      </div>
    );
  }

  private handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY > 0) {
      this.control.invokeAction(TreeOperations.next);
      e.stopPropagation();
    } else {
      this.control.invokeAction(TreeOperations.prior);
      e.stopPropagation();
    }
  };

  private handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      this.control.invokeAction(TreeOperations.prior);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    if (e.key === "ArrowDown") {
      this.control.invokeAction(TreeOperations.next);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    if (e.key === "ArrowRight") {
      this.control.invokeAction(TreeOperations.expand);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    if (e.key === "ArrowLeft") {
      this.control.invokeAction(TreeOperations.collaps);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    if (e.key === "End") {
      this.control.invokeAction(TreeOperations.lastPage);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }

    if (e.key === "Home") {
      this.control.invokeAction(TreeOperations.firstPage);
      e.stopPropagation();
      e.preventDefault();
      return false;
    }
  };

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.control.Tree.setActiveControlRequested();
    e.stopPropagation();
  };
}

function findColumnIndex(value: HTMLElement): number {
  let val = value;
  while (val) {
    if (val instanceof HTMLTableCellElement) {
      return val.cellIndex;
    }
    val = val.parentElement;
  }

  return 0;
}

const InnerTreeView = withContext(_InnerTreeView);

const baseRule = (props: K2RuleWithVCXProps) => ({
  flex: "1 1 auto",
  flexDirection: "column" as "column",
});

class _TreeView extends React.PureComponent<WithContextPlacementProps, K2ComponentState<UpdateHeadered>> {
  static displayName = `K2TreeView`;
  private control: NclTreeView;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclTreeView;
    }) as NclTreeView;
    this.state = { data: this.control.init(this) as UpdateHeadered, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateHeadered>) => {
      return { data: state as UpdateHeadered };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  render() {
    let addAttributes: ElementHtmlAttributes = getAttributes(this.state.data);
    let title = this.state.data.Title ? this.state.data.Title : "";
    if (this.state.data.TitleSuffix) {
      title += " " + this.state.data.TitleSuffix;
    }
    return (
      <div
        style={StyleHelper(this.control, { ...this.props.style, minHeight: this.control.ComputedMinHeightWithMargin + "px" })}
        className={this.props.renderer.renderRule(baseRule, { vcx: this.control.VCX })}
        onFocus={this.handleFocus}
      >
        {this.control.isShowHeader() && <K2Header controlUID={this.control.Header.MetaData.ControlUID} vrUID={this.props.vrUID} title={title} />}
        {this.control.Content && <InnerTreeView controlUID={this.control.Content.Ncl.ControlUID} vrUID={this.control.getRealizerUID()} />}
      </div>
    );
  }

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.control.setActiveControlRequested();
    e.stopPropagation();
  };
}

export const K2TreeView = withContext(_TreeView);

const treeViewStyles = (vcx: VisualContext) => `
  .ant-table {
    color: unset;
    font-size: unset;
    line-height: unset;
  }

  .ant-table-wrapper, .ant-spin-nested-loading, .ant-spin-container, .ant-table, .ant-table-container, .ant-table-body {
    flex: 1 1 auto;
  }

  .ant-table-body {
    overflow: auto !important;
  }

	.ant-table-container {
    flex-direction: column;
		border: 1px solid ${vcx.getColor(vcx.Data.ColorMap.ContentFrame1)};
    border-radius: 0px;
  }

  .ant-table-cell {
    padding: 0px !important;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: transparent;
  }

	.ant-table-header {
		border-bottom: 1px solid ${vcx.getColor(vcx.Data.ColorMap.ContentFrame1)};
    background-color: ${vcx.getColor(vcx.Data.ColorMap.ContentColorBck1)};
    flex: 0 0 auto;
	}

	.ant-table-small .ant-table-thead > tr > th {
		color: ${vcx.getColor(vcx.Data.ColorMap.ContentDecorateColorFrg)};
		background-color: ${vcx.getColor(vcx.Data.ColorMap.ContentColorBck1)};
	}

	.antd-denied-text {
		background-color: ${vcx.getColor(vcx.Data.ColorMap.ErrorColorBck)};
		color: ${vcx.getColor(vcx.Data.ColorMap.ErrorColorFrg)};
	}

	.antd-denied-text div {
		width: min-content;
		margin: auto;
	}

	.ant-table-tbody > tr > td {
		border-right: 1px solid ${vcx.getColor(vcx.Data.ColorMap.ContentFrame1)};
	}

	.border-top {
		border-top: 2px solid ${vcx.getColor(vcx.Data.ColorMap.BaseColorBck1)};
		border-bottom: 2px solid transparent;
		border-radius: 4px;
    border-left: 1px solid transparent;
	}

	.border-bottom {
		border-bottom: 2px solid ${vcx.getColor(vcx.Data.ColorMap.BaseColorBck1)};
		border-top: 2px solid transparent;
    border-radius: 4px;
    border-left: 1px solid transparent;
	}

	.border-transparent {
		border-top: 2px solid transparent;
    border-bottom: 2px solid transparent;
  }
  
  .border-select-node {
		border: 1px dashed ${vcx.getColor(vcx.Data.ColorMap.BaseColorBck1)} !important;
    padding: 1px 0;
    border-radius: 4px;
    background: ${vcx.getColor(vcx.Data.ColorMap.GridRulerColorBck)};
	}

  .ant-table-empty .ant-table-tbody > tr.ant-table-placeholder {
		color: ${vcx.getColor(vcx.Data.ColorMap.ContentNormalColorFrg)};
		background-color: ${vcx.getColor(vcx.Data.ColorMap.ContentChangeColorBck)};
	}

  .ant-table-row-expand-icon::after {
    top: 3px !important;
    bottom: 3px !important;
    left: 7px !important;
    opacity: 1 !important;
    background-color: currentColor !important;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 0px solid;
  }

  .ant-table.ant-table-small .ant-table-thead > tr > th {
    padding: 0 6px;
  }

  .ant-table.ant-table-small {
    font-size: inherit;
  }

  tr {
    height: ${vcx.GridControl.GetRowHeight(1)}px;
  }

  .ant-table .ant-table-container::before, .ant-table .ant-table-container::after {
    display: none;
  }
`;
